import React, { useState, useRef } from "react";
import QrScanner from "qr-scanner";
import { verifyTicket as callVerifyTicket, TicketResponse, formatDateTime, isFuture } from "../api/verifyTicket";

const Scanner = () => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [cameraActive, setCameraActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [responseData, setResponseData] = useState<TicketResponse | null>(null);

    const verifyTicket = async (barcodeData: string) => {
        setLoading(true);
        try {
            const data = await callVerifyTicket({ barcodeData });
            setResponseData(data);
            setShowModal(true);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = () => {
        navigator.mediaDevices
            .getUserMedia({ video: { facingMode: "environment" } })
            .then((stream) => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();

                    const qrScanner = new QrScanner(
                        videoRef.current,
                        (result) => {
                            const base64Result = btoa(result);
                            qrScanner.stop();
                            stream.getTracks().forEach((track) => track.stop());
                            setCameraActive(false);

                            verifyTicket(base64Result);
                        }
                    );
                    qrScanner.start();
                    setCameraActive(true);
                }
            })
            .catch((err) => {
                console.error("Errore nell'accesso alla fotocamera:", err);
            });
    };

    return (
        <div className="flex flex-col items-center p-4 w-full max-w-3xl mx-auto">
            <a href={"https://www.tper.it/"} className="mb-6 flex justify-center items-center w-full" >
                <img src="https://www.tper.it/maps/assets/logo.svg" className="max-w-[200px] w-full h-auto" alt="Logo TPER" />
            </a>

            <div className="border-2 p-4 rounded-[15px] border-[#e1333a] shadow-sm w-full sm:w-96 md:w-[28rem] flex flex-col items-center">
                {!cameraActive && !loading && (
                    <button onClick={handleClick} className="cursor-pointer bg-[#e1333a] rounded-[10px] shadow-sm px-6 py-3 mb-4 text-white font-semibold hover:bg-[#e1333a]-700 transition">
                        Scansiona QR Code
                    </button>
                )}

                {loading && (
                    <div className="mb-4 text-center">
                        <p className="font-semibold">Verifica in corso...</p>
                    </div>
                )}

                <div className="w-full rounded overflow-hidden">
                    <video ref={videoRef} className="w-full h-full object-cover" />
                </div>

            </div>

            {showModal && (
                <div className="fixed inset-0 bg-white rounded-[15px] bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white border-[#e1333a] border-2 p-6 rounded-[15px] shadow-sm max-w-md w-full mx-4">
                        <div className="mb-4">
                            <p className={`text-xl text-center font-bold mb-2 ${responseData?.success ? 'text-green-600' : 'text-red-600'}`}>
                                {responseData?.success ? 'Verifica completata' : 'Attenzione'}
                            </p>

                            {responseData?.errorMessage ? (
                                <p className="text-red-600 font-semibold text-center">
                                    {responseData.errorMessage}
                                </p>
                            ) : responseData?.status === null ? (
                                <p className="text-red-600 font-semibold text-center">
                                    Titolo non trovato o scaduto da più di 30 giorni
                                </p>
                            ) : (
                                <>
                                    <p>Tipo di biglietto: {responseData!.fare_description_extended}</p>
                                    <p>Numero seriale: {responseData!.serial_number}</p>
                                    <p>Numero di viaggi residui: {responseData!.residual_trip}</p>

                                    {responseData!.firstValidationDate && (
                                        <p>Titolo attivato il: {responseData!.firstValidationDate}</p>
                                    )}

                                    {responseData!.status !== 3 ? (
                                        <p className="text-red-600 font-semibold">
                                            {responseData!.formatted_status}
                                        </p>
                                    ) : (
                                        isFuture(responseData!.validity_end_date_time) ? (
                                            <p className="text-green-700 font-medium">
                                                Titolo attivo, valido fino alle{" "}
                                                {formatDateTime(responseData!.validity_end_date_time)}
                                            </p>
                                        ) : (responseData!.residual_trip ?? 0) > 0 ? (
                                            <p className="text-red-600 font-semibold">
                                                Il titolo deve essere convalidato
                                            </p>
                                        ) : (
                                            isFuture(responseData!.end_validity_date_time) ? (
                                                <div>
                                                    <p>
                                                        Scadenza:{" "}
                                                        {formatDateTime(responseData!.end_validity_date_time, "it-IT")}
                                                    </p>
                                                    <p className="text-green-700 font-semibold">
                                                        Titolo attivo
                                                    </p>
                                                </div>

                                            ) :
                                                <p className="text-red-600 font-semibold">
                                                    Titolo scaduto
                                                </p>
                                        )
                                    )}

                                    {((responseData!.residual_trip ?? 0) > 0 || !responseData!.validity_end_date_time) &&
                                        responseData!.end_validity_date_time && (
                                            <p className="text-sm text-gray-700 mt-2">
                                                Scadenza del supporto:{" "}
                                                {formatDateTime(responseData!.end_validity_date_time)}
                                            </p>
                                        )}
                                </>
                            )}
                        </div>

                        <div className="flex justify-center">
                            <button className="bg-[#e1333a] rounded-[10px] px-5 py-2 text-white font-semibold cursor-pointer" onClick={() => setShowModal(false)}>
                                Chiudi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Scanner;
